"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, DollarSign, ArrowRight, Briefcase, X } from "lucide-react";

interface ManualPositionSetupStepProps {
  nickname: string;
  onNicknameChange: (value: string) => void;
  balance: string;
  onBalanceChange: (value: string) => void;
  loading: boolean;
  onSubmit: () => void;
  onClose: () => void;
}

// Step 1 of the manual position modal: create the paper account.
export function ManualPositionSetupStep({
  nickname,
  onNicknameChange,
  balance,
  onBalanceChange,
  loading,
  onSubmit,
  onClose,
}: ManualPositionSetupStepProps) {
  return (
    <>
      <div className="flex justify-end -mb-3 -mt-1 -mr-2">
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          className="rounded-full p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mx-auto w-12 h-12 rounded-full bg-muted/60 border border-border/60 flex items-center justify-center">
        <Briefcase className="h-5 w-5 text-muted-foreground" />
      </div>

      <div className="space-y-1.5 text-center">
        <h2 className="text-lg tracking-tight" style={{ fontWeight: 600 }}>
          Set up your portfolio
        </h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Create a manual portfolio to track your positions and see personalized income opportunities.
        </p>
      </div>

      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="mp-nickname">Portfolio Nickname</Label>
          <Input
            id="mp-nickname"
            type="text"
            placeholder="My Portfolio"
            value={nickname}
            onChange={(e) => onNicknameChange(e.target.value)}
            disabled={loading}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="mp-balance" className="flex items-center gap-1">
            <DollarSign className="h-3.5 w-3.5" />
            Current Cash Balance
          </Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
            <Input
              id="mp-balance"
              type="number"
              min="0"
              step="1000"
              placeholder="100000"
              value={balance}
              onChange={(e) => onBalanceChange(e.target.value)}
              disabled={loading}
              className="pl-7"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-muted-foreground">Quick set:</span>
          {[10000, 25000, 50000, 100000, 250000, 500000].map((amt) => (
            <button
              key={amt}
              type="button"
              onClick={() => onBalanceChange(amt.toString())}
              disabled={loading}
              className="text-xs px-2 py-1 rounded-md bg-muted hover:bg-muted/80 transition-colors disabled:opacity-50"
            >
              ${(amt / 1000).toFixed(0)}k
            </button>
          ))}
        </div>
      </div>

      <Button
        size="lg"
        className="w-full h-11 bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700"
        disabled={loading}
        onClick={onSubmit}
      >
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Continue
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </>
  );
}
